// Invitation-code sign-up and admin password-reset codes, public at /signup.
// Design: docs/superpowers/specs/2026-10-19-invitation-signup-and-sms-sign-in-design.md
//   1. work email + code   2. choose a password   3. the second sign-in step
// An invitation code ('invite') creates the account. Step 3 sets up the authenticator
// app or, when text messages are on for sign-up, a mobile number that gets the codes.
// A reset code ('reset', from an RDV admin; /signup?reset=1) sets a new password, and
// step 3 is the person's usual sign-in code (app or text).
// Reuses the login screen's layout and styles (LoginModal.css / LoginPage.css).
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, updateMFAPreference } from 'aws-amplify/auth';
import QRCode from 'qrcode';
import { useFileContext } from '../../ContextAPI/FileContext';
import { buildOtpauthUri } from '../../utils/otpauth';
import { useAuthOptions } from '../../utils/authOptions';
import { isExpiredSignInSession } from '../Login/signInSessionRetry';
import { phoneTail } from '../Login/codeDelivery';
import { restartSmsChallenge, smsDestination, SMS_STEP } from '../Login/smsSignIn';
import ResendCodeButton from '../Login/ResendCodeButton';
import PasswordToggle from '../Login/PasswordToggle';
import PhoneConsentFields from '../Sms/PhoneConsentFields';
import { toE164, badPhoneMessage } from '../Sms/smsConsent';
import rdv from '../../assets/rdv-logoW.png';
import MfaCodeBoxes from './MfaCodeBoxes';
import { formatInvitationCode } from './invitationCode';
import { PASSWORD_RULES, passwordProblem } from './passwordRules';
import {
  verifyInvitation, setSignupPassword, setSignupPhone, completeSignup, errorMessage,
} from './signupApi';
import '../Login/LoginModal.css';
import '../Login/LoginPage.css';
import './SignupPage.css';

// Every step after the password counts as step 3: the method choice ('method'), the
// number ('phone'), a texted code ('sms'), app setup ('totp') or a reset's app code ('app').
const STEP_NUMBER = { invitation: 1, password: 2 };

function subtitleFor(step, reset, smsTo) {
  const tail = phoneTail(smsTo);
  return {
    invitation: reset
      ? 'Enter your work email and the password reset code you were given.'
      : 'Enter your work email and the invitation code you were given.',
    password: reset ? 'Choose a new password.' : 'Choose a password for your account.',
    method: 'How do you want to get your sign-in codes?',
    phone: "Enter your mobile number. We'll text you a code to confirm it.",
    sms: `Enter the 6-digit code we texted to ${tail ? `the number ending in ${tail}` : 'your phone'}.`,
    totp: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows.',
    app: 'Enter the 6-digit code from your authenticator app to finish.',
  }[step];
}

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useFileContext();
  const options = useAuthOptions();
  const [purpose, setPurpose] = useState(
    () => (new URLSearchParams(location.search).has('reset') ? 'reset' : 'invite'));
  const [step, setStep] = useState('invitation');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [phone, setPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [smsTo, setSmsTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The rest happens on the sign-in page: a timed-out step, or an account that
  // doesn't need this page's step 3 (e.g. it already has one).
  const [showSignInLink, setShowSignInLink] = useState(false);

  const reset = purpose === 'reset';
  const cleanEmail = email.trim().toLowerCase();
  const cleanCode = code.trim();
  const credentials = { username: cleanEmail, password };
  const international = options?.sms_international === true;

  // Runs `work` with the busy flag on; a failure shows the backend's message.
  const run = async (work) => {
    setError('');
    setBusy(true);
    try {
      await work();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const goToSignIn = (message) => {
    setShowSignInLink(true);
    setError(message);
  };

  const submitInvitation = async (event) => {
    event.preventDefault();
    if (!cleanEmail || !cleanCode) {
      setError(reset ? 'Enter your work email and reset code.' : 'Enter your work email and invitation code.');
      return;
    }
    await run(async () => {
      setPurpose(await verifyInvitation(cleanEmail, cleanCode));
      setStep('password');
    });
  };

  const showAuthenticatorSetup = async (totpSetupDetails) => {
    const shared = totpSetupDetails.sharedSecret;
    setSecret(shared);
    setQr(await QRCode.toDataURL(
      buildOtpauthUri({ secret: shared, email: cleanEmail, issuer: 'RDV Systems' }),
      { width: 200, margin: 2 },
    ));
    setMfaCode('');
    setStep('totp');
  };

  const startAuthenticatorSetup = async () => {
    const { isSignedIn, nextStep } = await signIn(credentials);
    if (isSignedIn || nextStep?.signInStep !== 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      // The password is set; anything other than a fresh authenticator setup is the
      // sign-in page's job, not an error.
      goToSignIn('Your password is set. Sign in to finish.');
      return;
    }
    await showAuthenticatorSetup(nextStep.totpSetupDetails);
  };

  const showTextedCode = (nextStep) => {
    setSmsTo(smsDestination(nextStep));
    setMfaCode('');
    setStep('sms');
  };

  const finish = async () => {
    try {
      await completeSignup();
    } catch (err) {
      // Not fatal: the account works; the unused code simply expires.
      console.warn('signup: could not mark the invitation used', err);
    }
    setAuth(true, cleanEmail);
    navigate('/projects', { replace: true });
  };

  // A reset code changed the password: confirm with the person's usual sign-in code.
  const continueReset = async () => {
    const { isSignedIn, nextStep } = await signIn(credentials);
    if (isSignedIn) {
      await finish();
      return;
    }
    switch (nextStep?.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setMfaCode('');
        setStep('app');
        break;
      case SMS_STEP:
        showTextedCode(nextStep);
        break;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        await showAuthenticatorSetup(nextStep.totpSetupDetails);
        break;
      default:
        goToSignIn('Your new password is set. Sign in to finish.');
    }
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    const problem = passwordProblem(password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    await run(async () => {
      await setSignupPassword(cleanEmail, cleanCode, password);
      if (reset) await continueReset();
      else if (options?.sms_signup) setStep('method');
      else await startAuthenticatorSetup();
    });
  };

  const chooseText = () => {
    setError('');
    setStep('phone');
  };

  const chooseApp = () => run(startAuthenticatorSetup);

  const submitPhone = async (event) => {
    event.preventDefault();
    const e164 = toE164(phone, { international });
    if (!e164) {
      setError(badPhoneMessage(international));
      return;
    }
    if (!agreed) {
      setError('Tick the box to agree to receive sign-in codes by text message.');
      return;
    }
    await run(async () => {
      await setSignupPhone(cleanEmail, cleanCode, e164);
      // Signing in now makes Cognito text the first code: proof they hold the phone.
      const { isSignedIn, nextStep } = await signIn(credentials);
      if (isSignedIn || nextStep?.signInStep !== SMS_STEP) {
        goToSignIn('Your password and number are set. Sign in to finish.');
        return;
      }
      showTextedCode(nextStep);
    });
  };

  // A new sign-in is what texts a fresh code.
  const resendCode = async () => {
    setSmsTo(await restartSmsChallenge({ credentials, signIn }));
    setMfaCode('');
    setError('');
  };

  const changeNumber = () => {
    setAgreed(false);
    setMfaCode('');
    setError('');
    setStep('phone');
  };

  // The corner back button: one step back while that step can still be redone,
  // otherwise the sign-in page (once the password is set, signing in finishes the rest).
  const goBack = () => {
    setError('');
    if (step === 'password' && !showSignInLink) setStep('invitation');
    else if (step === 'phone' && !smsTo && !showSignInLink) setStep('method');
    else navigate('/login');
  };

  // A different number needs its own consent: any edit unticks the box.
  const editPhone = (value) => {
    if (value !== phone) setAgreed(false);
    setPhone(value);
  };

  const explainCodeFailure = async (err) => {
    const sessionExpired = isExpiredSignInSession(err);
    const codeExpired = (err?.name || err?.code) === 'ExpiredCodeException';
    if (step === 'sms' && (sessionExpired || codeExpired)) {
      try {
        await resendCode();
        setError('That code expired, so we texted you a new one. Enter the new code.');
        return;
      } catch (restartErr) {
        console.warn('signup: could not text a new code', restartErr);
      }
    }
    if (sessionExpired || (step === 'sms' && codeExpired)) {
      goToSignIn(step === 'totp'
        ? 'This setup step timed out. Sign in with your email and new password to finish setting up your authenticator app.'
        : 'This step timed out. Sign in with your email and new password to finish.');
      return;
    }
    setError(step === 'sms'
      ? 'That code was incorrect. Enter the 6-digit code we texted you.'
      : 'That code was incorrect or expired. Enter the current 6-digit code.');
  };

  const confirmCode = async (value = mfaCode) => {
    if (value.length !== 6) {
      setError(step === 'sms'
        ? 'Enter the 6-digit code we texted you.'
        : 'Enter the 6-digit code from your authenticator app.');
      return;
    }
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: value });
      if (!isSignedIn) throw new Error('Sign-in did not complete');
      if (step === 'totp') {
        try {
          await updateMFAPreference({ totp: 'PREFERRED' });
        } catch (prefErr) {
          // Best-effort: the person is already signed in, so a failed preference call must not look like a bad code.
          console.warn('signup: could not set the authenticator app as preferred', prefErr);
        }
      }
      await finish();
    } catch (err) {
      setMfaCode('');
      await explainCodeFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const errorLine = error && <span className="error-text signup-error" role="alert">{error}</span>;
  const signInLink = (
    <div className="forgot-password">
      <Link to="/login" className="forgot-link">Go to sign in</Link>
    </div>
  );
  const codeBoxes = (label) => (
    <div className="input-group">
      <span className="input-label">{label}</span>
      <MfaCodeBoxes
        value={mfaCode}
        onChange={(next) => { setMfaCode(next); if (error) setError(''); }}
        onComplete={confirmCode}
        disabled={busy || showSignInLink}
        hasError={!!error}
      />
    </div>
  );
  const finishButton = showSignInLink ? signInLink : (
    <div className="mfa-buttons">
      <button type="button" className="signin-button" onClick={() => confirmCode()} disabled={busy}>
        {busy ? 'Verifying…' : 'Finish'}
      </button>
    </div>
  );

  return (
    <div className="login-page">
      <div className="login-page-inner">
        <div className="modal-container">
          <div className="modern-modal auth-modal signup-modal">
            <div className="image-section">
              <div className="logo-container">
                <img src={rdv} alt="RDV Systems" className="rdv-logo" />
              </div>
            </div>

            <div className="login-section auth-panel">
              {/* Same corner button as the sign-in page (LoginModal.css) */}
              <button type="button" className="login-close-button" onClick={goBack} aria-label="Back">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    strokeLinejoin="round" />
                </svg>
              </button>
              <div className="login-content auth-panel-content">
                <div className="login-header login-form-header">
                  <p className="signup-step">Step {STEP_NUMBER[step] || 3} of 3</p>
                  <h1 className="title">{reset ? 'Reset your password' : 'Create your account'}</h1>
                  <p className="subtitle">{subtitleFor(step, reset, smsTo)}</p>
                </div>

                {step === 'invitation' && (
                  <form className="login-form" onSubmit={submitInvitation} noValidate>
                    <div className="input-group">
                      <label htmlFor="signup-email" className="input-label">Work email</label>
                      <div className="input-wrapper">
                        <input id="signup-email" type="email" className="glass-input" autoComplete="email"
                          value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
                      </div>
                    </div>
                    <div className="input-group">
                      <label htmlFor="signup-code" className="input-label">
                        {reset ? 'Password reset code' : 'Invitation code'}
                      </label>
                      <div className="input-wrapper">
                        <input id="signup-code" type="text" className="glass-input signup-code-input"
                          autoComplete="off" autoCapitalize="characters" spellCheck={false}
                          placeholder="XXX-XXXX"
                          value={code} onChange={(e) => setCode(formatInvitationCode(e.target.value))}
                          disabled={busy} />
                      </div>
                      <span className="signup-hint">
                        {reset
                          ? 'It’s in the email from your administrator, or ask them for it.'
                          : 'It’s in your invitation email, or ask the person who added you.'}
                      </span>
                    </div>
                    {errorLine}
                    <button type="submit" className="signin-button" disabled={busy}>
                      {busy ? 'Checking…' : 'Continue'}
                    </button>
                    <div className="forgot-password">
                      <Link to="/login" className="forgot-link">Already have an account? Sign in</Link>
                    </div>
                  </form>
                )}

                {step === 'password' && (
                  <form className="login-form" onSubmit={submitPassword} noValidate>
                    <div className="input-group">
                      <label htmlFor="signup-password" className="input-label">New password</label>
                      <div className="input-wrapper">
                        <input id="signup-password" type={showPassword ? 'text' : 'password'} className="glass-input"
                          autoComplete="new-password"
                          value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy || showSignInLink} />
                        <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)}
                          label="new password" />
                      </div>
                    </div>
                    <div className="input-group">
                      <label htmlFor="signup-confirm" className="input-label">Confirm password</label>
                      <div className="input-wrapper">
                        <input id="signup-confirm" type={showConfirm ? 'text' : 'password'} className="glass-input"
                          autoComplete="new-password"
                          value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy || showSignInLink} />
                        <PasswordToggle visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)}
                          label="confirmed password" />
                      </div>
                    </div>
                    <ul className="signup-rules" aria-label="Password requirements">
                      {PASSWORD_RULES.map((rule) => (
                        <li key={rule.id} className={rule.test(password) ? 'met' : ''}>{rule.label}</li>
                      ))}
                    </ul>
                    {errorLine}
                    {showSignInLink ? signInLink : (
                      <button type="submit" className="signin-button" disabled={busy}>
                        {busy ? 'Saving…' : 'Continue'}
                      </button>
                    )}
                  </form>
                )}

                {step === 'method' && (
                  <div className="signup-methods" role="group" aria-label="Sign-in method">
                    <button type="button" className="signup-method" onClick={chooseText}
                      disabled={busy || showSignInLink}>
                      <span className="signup-method__title">Text message</span>
                      <span className="signup-method__text">We text you a code each time you sign in.</span>
                    </button>
                    <button type="button" className="signup-method" onClick={chooseApp}
                      disabled={busy || showSignInLink}>
                      <span className="signup-method__title">Authenticator app</span>
                      <span className="signup-method__text">
                        An app such as Microsoft or Google Authenticator shows the code.
                      </span>
                    </button>
                    {errorLine}
                    {showSignInLink && signInLink}
                  </div>
                )}

                {step === 'phone' && (
                  <form className="login-form" onSubmit={submitPhone} noValidate>
                    <PhoneConsentFields idPrefix="signup" phone={phone} onPhoneChange={editPhone}
                      agreed={agreed} onAgreedChange={setAgreed} disabled={busy || showSignInLink} />
                    {errorLine}
                    {showSignInLink ? signInLink : (
                      <button type="submit" className="signin-button" disabled={busy}>
                        {busy ? 'Sending…' : 'Send code'}
                      </button>
                    )}
                    {!showSignInLink && !smsTo && (
                      <p className="signup-hint signup-alt">
                        Text messages are optional.{' '}
                        <button type="button" className="signup-link-button"
                          onClick={() => { setError(''); setStep('method'); }}>
                          Use an authenticator app instead
                        </button>
                      </p>
                    )}
                  </form>
                )}

                {step === 'sms' && (
                  <div className="mfa-verify-container">
                    {codeBoxes('6-digit code from the text message')}
                    {errorLine}
                    {finishButton}
                    {!showSignInLink && (
                      <div className="signup-sms-links">
                        <ResendCodeButton onResend={resendCode}
                          onError={() => goToSignIn("We couldn't send a new code. Sign in to get one.")} />
                        {!reset && (
                          <button type="button" className="signup-link-button" onClick={changeNumber}>
                            Wrong number? Change it
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {step === 'totp' && (
                  <div className="mfa-verify-container mfa-with-qr">
                    <div className="qr-code-container">
                      <img src={qr} alt="QR code for your authenticator app" className="qr-code-image" />
                    </div>
                    <p className="signup-secret">
                      Can&apos;t scan it? Enter this key in the app: <code>{secret}</code>
                    </p>
                    {codeBoxes('6-digit code from the app')}
                    {errorLine}
                    {finishButton}
                  </div>
                )}

                {step === 'app' && (
                  <div className="mfa-verify-container">
                    {codeBoxes('6-digit code from the app')}
                    {errorLine}
                    {finishButton}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
